import {
  useWSModalCustom,
  useWSSnackbar,
  WSElement,
  WSForm,
  WSGrid,
  WSInputText,
  WSInputTextarea,
  WSList,
  WSModal,
  WSModalCustomProps,
  WSText
} from "@wingspanhq/fe-component-library";
import * as Yup from "yup";

import { Engagement, PayeeEngagementType } from "@wingspanhq/wingspan-api-sdk";
import { queriesEngagements } from "@query";
import { ErrorAlert } from "@components/ErrorAlert";
import { useGoToEngagementTypeDetails } from "../../paths";

export interface CreateEngagementFormValues {
  name: string;
  description?: string;
}

export type ModalCreateEngagementProps =
  WSModalCustomProps<Engagement | void> & {
    /** Navigate to the new engagement's details screen after creating it. */
    redirectToDetails?: boolean;
  };

const ModalCreateEngagement: React.FC<ModalCreateEngagementProps> = props => {
  const { openSnackbar } = useWSSnackbar();
  const [createEngagement, createEngagementMeta] =
    queriesEngagements.createEngagement.useMutation();
  const goToDetails = useGoToEngagementTypeDetails();

  const onSubmit = async (data: CreateEngagementFormValues) => {
    const engagement = await createEngagement({
      engagementCreate: {
        ...data
      }
    });

    if (!engagement) {
      return;
    }

    openSnackbar({
      message: "Engagement created successfully",
      type: "success"
    });

    props.onClose(engagement);

    if (props.redirectToDetails && engagement.id) {
      goToDetails(engagement.id);
    }
  };

  return (
    <WSForm<CreateEngagementFormValues>
      validationSchema={Yup.object().shape({
        name: Yup.string().required("Required")
      })}
      onSubmit={onSubmit}
    >
      <WSModal
        title="Add engagement"
        size="L"
        fullScreenOnMobile
        onClose={props.onClose}
        actions={{
          buttons: [
            {
              label: "Create",
              kind: "Primary",
              type: "submit",
              loading: createEngagementMeta.isLoading
            },
            {
              label: "Cancel",
              kind: "Tertiary",
              type: "button",
              onClick: () => props.onClose()
            }
          ]
        }}
      >
        <WSList gap="L">
          <WSText.Paragraph weight="medium">
            Engagement information
          </WSText.Paragraph>

          <WSGrid gutter={{ m: "2XL" }}>
            <WSGrid.Item span={{ m: "6", s: "12", xs: "12" }}>
              <WSForm.Field
                name="name"
                label="Engagement name"
                placeholder="Engagement name"
                component={WSInputText}
                componentProps={{
                  required: true
                }}
              />
            </WSGrid.Item>

            <WSGrid.Item span={{ m: "6", s: "12", xs: "12" }}>
              <WSElement>
                <WSText.ParagraphSm weight="book" color="gray500" mb="S">
                  Type
                </WSText.ParagraphSm>
                <WSText.ParagraphSm weight="book" color="gray700">
                  Independent Contractor (IC)
                </WSText.ParagraphSm>
              </WSElement>
            </WSGrid.Item>

            <WSGrid.Item span={{ m: "12" }}>
              <WSForm.Field
                name="description"
                label="Description"
                placeholder="Engagement description"
                component={WSInputTextarea}
              />
            </WSGrid.Item>
          </WSGrid>

          <ErrorAlert context="Engagement" error={createEngagementMeta.error} />

          <WSText.ParagraphXs weight="book" color="gray400">
            Once the engagement has been created, you can add requirements,
            contractors, and members to it.
          </WSText.ParagraphXs>
        </WSList>
      </WSModal>
    </WSForm>
  );
};

export function useCreateEngagementModal() {
  return useWSModalCustom(ModalCreateEngagement);
}
